import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useAuth } from '../../../app/provider';

const LoginForm: React.FC = () => {
  const [step, setStep] = useState<'mobile' | 'otp'>('mobile');
  const [mobileNo, setMobileNo] = useState('');
  const [otp, setOtp] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { sendOtp, verifyOtp } = useAuth();

  const validateMobileNumber = (mobile: string): boolean => {
    const mobileRegex = /^[6-9]\d{9}$/;
    return mobileRegex.test(mobile);
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateMobileNumber(mobileNo)) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await sendOtp(mobileNo);
      setSessionId(response.session_id);
      setStep('otp');
    } catch (err: any) {
      setError(err?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await verifyOtp(sessionId, otp);
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToMobile = () => {
    setStep('mobile');
    setOtp('');
    setSessionId('');
    setError('');
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* Left visual panel with image */}
      <aside className="relative hidden lg:block">
        <div
          className="absolute inset-0 bg-gray-100 bg-center bg-cover"
          style={{ backgroundImage: "url('/images/login-side.png')" }}
        />
        <div className="relative z-10 flex h-full flex-col justify-between p-8 bg-white/30 backdrop-blur-[1px]">
          <div className="flex items-center gap-2">
            <img src="/images/white-logo.png" alt="MRC Logistics Logo" className="h-12 w-auto" />
          </div>
        </div>
      </aside>

      {/* Right auth panel */}
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm border rounded-lg p-6 shadow-sm bg-card">
          <div className="mb-6 text-center">
            <h1 className="text-lg font-semibold">MRC Customer Portal</h1>
            <p className="text-sm text-muted-foreground">
              {step === 'mobile' ? 'Enter your mobile number' : 'Enter the OTP sent to your mobile'}
            </p>
          </div>
          
          {error && (
            <div className="mb-4 text-sm text-red-600">
              {error}
            </div>
          )}

          {step === 'mobile' ? (
            <form onSubmit={handleSendOtp} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="mobile" className="text-sm font-medium">Mobile Number</label>
                <Input
                  id="mobile"
                  type="tel"
                  value={mobileNo}
                  onChange={(e) => setMobileNo(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Enter 10-digit mobile number"
                  autoComplete="tel"
                  disabled={loading}
                  required
                  maxLength={10}
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="otp" className="text-sm font-medium">
                  OTP sent to +91 {mobileNo}
                </label>
                <Input
                  id="otp"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit OTP"
                  autoComplete="one-time-code"
                  disabled={loading}
                  required
                  maxLength={6}
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Verifying...' : 'Verify OTP'}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                className="w-full" 
                onClick={handleBackToMobile}
                disabled={loading}
              >
                Change Mobile Number
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
